import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import FadeIn from '../components/FadeIn';
import SEO from '../components/SEO';
import { cloudinaryUrl } from '../utils/cloudinary';
import allProducts from '../data/products.json';

const CATEGORIES = [
  { slug: '',           label: 'Összes' },
  { slug: 'elektromos', label: 'Elektromos kerékpár' },
  { slug: 'tura',       label: 'Túrakerékpár' },
  { slug: 'varosi',     label: 'Városi kerékpár' },
  { slug: 'gyermek',    label: 'Gyermekkerékpár' },
  { slug: 'mtb',        label: 'MTB kerékpár' },
  { slug: 'haromkereku', label: 'Háromkerekű' },
];

const CAT_CARDS = [
  { slug: 'elektromos', label: 'Elektromos', sub: 'kerékpár', bg: '#1A1A3E', accent: '#4F4FBF' },
  { slug: 'tura',       label: 'Túra',       sub: 'kerékpár', bg: '#0D3320', accent: '#2D7A4F' },
  { slug: 'varosi',     label: 'Városi',     sub: 'kerékpár', bg: '#C85C48', accent: '#E8A455' },
  { slug: 'gyermek',    label: 'Gyermek',    sub: 'kerékpár', bg: '#7A3A00', accent: '#E8A455' },
  { slug: 'mtb',        label: 'MTB',        sub: 'kerékpár', bg: '#1C3A0D', accent: '#4CAF50' },
  { slug: 'haromkereku', label: 'Háromkerekű', sub: 'kerékpár', bg: '#2C1654', accent: '#9C27B0' },
];

const SORT_OPTIONS = [
  { value: '',           label: 'Alapértelmezett' },
  { value: 'price_asc',  label: 'Ár: alacsony → magas' },
  { value: 'price_desc', label: 'Ár: magas → alacsony' },
  { value: 'name_asc',   label: 'Név (A–Z)' },
];

const LIMIT = 9;

// Kategória darabszámok előre kiszámolva
const catCounts = {};
CATEGORIES.slice(1).forEach(c => {
  catCounts[c.slug] = allProducts.filter(p => p.category_slug === c.slug).length;
});

function CategoryCards({ onSelect }) {
  const [hov, setHov] = useState(null);
  return (
    <div style={{ marginBottom: 'var(--sp-8)' }}>
      <p style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--text-muted)', marginBottom: 'var(--sp-4)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        Szűrj kategória szerint
      </p>
      <div className="shop-cat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--sp-3)' }}>
        {CAT_CARDS.map(cat => (
          <button key={cat.slug} onClick={() => onSelect(cat.slug)}
            onMouseEnter={() => setHov(cat.slug)} onMouseLeave={() => setHov(null)}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', width: '100%' }}
          >
            <div style={{
              aspectRatio: '4/3', borderRadius: 'var(--r-xl)', background: cat.bg,
              position: 'relative', overflow: 'hidden',
              transform: hov === cat.slug ? 'translateY(-3px)' : 'translateY(0)',
              boxShadow: hov === cat.slug ? `0 12px 32px ${cat.bg}88` : '0 2px 8px rgba(0,0,0,0.12)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              padding: 'clamp(12px, 2.5vw, 20px)',
            }}>
              <div style={{ position: 'absolute', right: '-20%', top: '-20%', width: '70%', aspectRatio: '1', borderRadius: '50%', background: cat.accent, opacity: hov === cat.slug ? 0.25 : 0.12, transition: 'opacity 0.3s ease' }} />
              <span style={{ fontSize: 'clamp(0.6rem, 1.2vw, 0.75rem)', fontWeight: 600, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.06em', position: 'relative', zIndex: 1 }}>
                {catCounts[cat.slug] ?? 0} modell
              </span>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.1rem, 2.5vw, 1.75rem)', fontWeight: 900, color: 'white', lineHeight: 1.05, letterSpacing: '-0.02em' }}>{cat.label}</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.1rem, 2.5vw, 1.75rem)', fontWeight: 900, color: cat.accent, lineHeight: 1.05, letterSpacing: '-0.02em' }}>{cat.sub}</div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function RecentlyViewedBar({ items, onClear }) {
  if (!items || !items.length) return null;
  return (
    <div style={{ background: 'var(--white)', borderRadius: 'var(--r-xl)', padding: 'var(--sp-4) var(--sp-5)', marginBottom: 'var(--sp-6)', border: '1px solid var(--border)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--sp-3)' }}>
        <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--text)' }}>Legutóbb megnézted</span>
        <button onClick={onClear} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', padding: '2px 6px' }}>Törlés</button>
      </div>
      <div style={{ display: 'flex', gap: 'var(--sp-3)', overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 4 }}>
        {items.map(p => (
          <Link key={p.slug} to={`/kerekparok/${p.slug}`} style={{ flexShrink: 0, width: 150, textDecoration: 'none', color: 'inherit' }}>
            <div style={{ width: 150, height: 110, borderRadius: 'var(--r-md)', overflow: 'hidden', background: 'var(--bg)', marginBottom: 'var(--sp-2)' }}>
              {p.image_url && <img src={cloudinaryUrl(p.image_url, { width: 300 })} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
            </div>
            <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
            </Link>
        ))}
      </div>
    </div>
  );
}

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputVal, setInputVal] = useState(searchParams.get('search') || '');
  const [search, setSearch]     = useState(searchParams.get('search') || '');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const category = searchParams.get('category') || '';
  const sort     = searchParams.get('sort')     || '';
  const page     = parseInt(searchParams.get('page') || '1', 10);

  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    setSearch(urlSearch);
    setInputVal(urlSearch);
  }, [searchParams]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  // Kliens oldali szűrés + rendezés
  const filtered = useMemo(() => {
    let list = [...allProducts];
    if (category) list = list.filter(p => p.category_slug === category);
    if (search)   list = list.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));
    if (sort === 'price_asc')  list.sort((a, b) => a.price - b.price);
    if (sort === 'price_desc') list.sort((a, b) => b.price - a.price);
    if (sort === 'name_asc')   list.sort((a, b) => a.name.localeCompare(b.name, 'hu'));
    return list;
  }, [category, search, sort]);

  const total      = filtered.length;
  const totalPages = Math.ceil(total / LIMIT);
  const products   = filtered.slice((page - 1) * LIMIT, page * LIMIT);
  const hasFilters = !!(category || search || sort);
  const currentLabel = CATEGORIES.find(c => c.slug === category)?.label || 'Összes';

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(inputVal);
    setParam('search', inputVal);
  };

  const clearAll = () => {
    setSearchParams({});
    setSearch('');
    setInputVal('');
    setDrawerOpen(false);
  };

  // Recently viewed (localStorage)
  const [recentItems, setRecentItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem('recently_viewed') || '[]'); } catch { return []; }
  });
  const clearRecent = () => { localStorage.removeItem('recently_viewed'); setRecentItems([]); };

  const Sidebar = () => (
    <>
      <p className="filter-section-title">Kategória</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', marginBottom: 'var(--sp-5)' }}>
        {CATEGORIES.map(c => (
          <button key={c.slug} onClick={() => { setParam('category', c.slug); setDrawerOpen(false); }}
            style={{ padding: '9px 14px', borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)', fontWeight: c.slug === category ? 600 : 400, color: c.slug === category ? 'var(--coral)' : 'var(--text-muted)', background: c.slug === category ? 'var(--coral-light)' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'all var(--t-fast)' }}
          >{c.label}</button>
        ))}
      </div>
      <div className="filter-divider" />
      <p className="filter-section-title">Rendezés</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
        {SORT_OPTIONS.map(o => (
          <button key={o.value} onClick={() => { setParam('sort', o.value); setDrawerOpen(false); }}
            style={{ padding: '9px 14px', borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)', fontWeight: o.value === sort ? 600 : 400, color: o.value === sort ? 'var(--coral)' : 'var(--text-muted)', background: o.value === sort ? 'var(--coral-light)' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'all var(--t-fast)' }}
          >{o.label}</button>
        ))}
      </div>
      {hasFilters && (
        <>
          <div className="filter-divider" />
          <button className="btn btn-ghost btn-full" onClick={clearAll}>Szűrők törlése</button>
        </>
      )}
    </>
  );

  return (
    <>
      <SEO url="/kerekparok" title="Kerékpárok – 300+ Modell Raktárról" description="Válogass 300+ kerékpár modell közül: elektromos e-bike, túrakerékpár, MTB mountain bike, városi city bike, gyermek és háromkerekű bringák." />
      <main className="page">
        <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: 'var(--sp-8) 0 var(--sp-6)' }}>
          <div className="container">
            <div className="breadcrumb" style={{ marginBottom: 'var(--sp-4)' }}>
              <a href="/">Kezdőlap</a>
              <span className="breadcrumb__sep">›</span>
              <span style={{ color: 'var(--text)', fontWeight: 500 }}>Kerékpárok</span>
              {category && (<><span className="breadcrumb__sep">›</span><span style={{ color: 'var(--coral)', fontWeight: 500 }}>{currentLabel}</span></>)}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
              <div>
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.6rem,4vw,2.4rem)', fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.02em' }}>
                  {category ? currentLabel : 'Összes kerékpár'}
                </h1>
                <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', marginTop: 4 }}>{total} termék</p>
              </div>
              <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flex: 1, minWidth: 0 }}>
                <form onSubmit={handleSearch} style={{ position: 'relative', display: 'flex', gap: 6, flex: 1, minWidth: 0 }}>
                  <input className="form-input" style={{ height: 42, borderRadius: 'var(--r-full)', paddingLeft: 36, paddingRight: 12, flex: 1, minWidth: 0 }}
                    type="text" placeholder="Kerékpár neve..." value={inputVal} onChange={e => setInputVal(e.target.value)} />
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)', pointerEvents: 'none' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                  </span>
                  <button type="submit" className="btn btn-primary btn-sm" style={{ flexShrink: 0 }}>Keresés</button>
                </form>
              </div>
            </div>
            {hasFilters && (
              <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-4)', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', fontWeight: 500 }}>Szűrők:</span>
                {category && <button className="badge badge-coral" onClick={() => setParam('category', '')} style={{ cursor: 'pointer' }}>{currentLabel} ×</button>}
                {search && <button className="badge badge-coral" onClick={() => { setSearch(''); setInputVal(''); setParam('search', ''); }} style={{ cursor: 'pointer' }}>"{search}" ×</button>}
                {sort && <button className="badge badge-gray" onClick={() => setParam('sort', '')} style={{ cursor: 'pointer' }}>{SORT_OPTIONS.find(o => o.value === sort)?.label} ×</button>}
                <button onClick={clearAll} style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', fontWeight: 500, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer' }}>Törlés</button>
              </div>
            )}
          </div>
        </div>

        <div className="container" style={{ padding: 'var(--sp-8) var(--container-pad)' }}>
          <div className="shop-layout">
            <aside className="shop-sidebar"><Sidebar /></aside>
            <div style={{ minWidth: 0, overflow: 'hidden' }}>
              <RecentlyViewedBar items={recentItems} onClear={clearRecent} />
              {!category && !search && (
                <>
                  <CategoryCards onSelect={slug => setParam('category', slug)} />
                  <div className="quiz-cta-bar">
                    <span>🤔 Nem tudod melyiket válaszd?</span>
                    <Link to="/kviz">Segítünk megtalálni →</Link>
                  </div>
                </>
              )}
              {products.length > 0 ? (
                <>
                  <div className="product-grid">
                    {products.map((p, i) => (
                      <FadeIn key={p.id} direction="up" delay={i * 0.05} threshold={0.05}>
                        <ProductCard product={p} />
                      </FadeIn>
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button className="page-btn" disabled={page <= 1} onClick={() => setParam('page', page - 1)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
                      </button>
                      {(() => {
                        const pages = [];
                        const rangeStart = Math.max(2, page - 1);
                        const rangeEnd   = Math.min(totalPages - 1, page + 1);
                        pages.push(1);
                        if (rangeStart > 2) pages.push('...');
                        for (let p = rangeStart; p <= rangeEnd; p++) pages.push(p);
                        if (rangeEnd < totalPages - 1) pages.push('...');
                        if (totalPages > 1) pages.push(totalPages);
                        return pages.map((p, i) =>
                          p === '...'
                            ? <span key={`e${i}`} style={{ padding: '0 4px', color: 'var(--text-muted)', alignSelf: 'center' }}>…</span>
                            : <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => setParam('page', p)}>{p}</button>
                        );
                      })()}
                      <button className="page-btn" disabled={page >= totalPages} onClick={() => setParam('page', page + 1)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <div className="empty-state__icon">🔍</div>
                  <h3 className="empty-state__title">Nincs találat</h3>
                  <p style={{ marginBottom: 'var(--sp-6)' }}>{search ? `Nem találtunk terméket erre: "${search}"` : 'Próbálj más szűrőt'}</p>
                  <button className="btn btn-primary" onClick={clearAll}>Szűrők törlése</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {drawerOpen && (
          <>
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 600 }} onClick={() => setDrawerOpen(false)} />
            <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 601, background: 'var(--white)', borderRadius: 'var(--r-2xl) var(--r-2xl) 0 0', padding: 'var(--sp-6)', maxHeight: '85vh', overflowY: 'auto', boxShadow: 'var(--shadow-xl)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--sp-5)' }}>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xl)', fontWeight: 700 }}>Szűrők</span>
                <button onClick={() => setDrawerOpen(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <Sidebar />
            </div>
          </>
        )}
      </main>
    </>
  );
}
